import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Megaphone, Users, Clock, CheckCircle, Mail, Bell, AlertCircle, Trash2, RefreshCw, Edit } from 'lucide-react';
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, 
  DialogTitle, DialogFooter 
} from '@/components/ui/dialog';
import { api } from '../../App';
import { toast } from 'sonner';
import { useEffect } from 'react';

export default function AnnouncementsTab() {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [fetchingAnnouncements, setFetchingAnnouncements] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [updating, setUpdating] = useState(false);

  const recipientOptions = [
    { value: 'all', label: 'All Users', icon: Users, description: 'Send to everyone' },
    { value: 'trial', label: 'Trial Users', icon: Clock, description: 'Only users in trial period' },
    { value: 'active', label: 'Active Subscribers', icon: CheckCircle, description: 'Only paid subscribers' },
    { value: 'expired', label: 'Expired Users', icon: Clock, description: 'Trial or subscription expired' },
  ];

  const handleSendAnnouncement = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/admin/announcements/send', {
        title: title.trim(),
        message: message.trim(),
        recipient_type: recipientType,
        send_email: sendEmail
      });

      toast.success(
        `Announcement sent! ${response.data.notifications_created} notifications created` +
        (sendEmail ? `, ${response.data.emails_sent} emails sent` : '')
      );

      // Reset form
      setTitle('');
      setMessage('');
      setRecipientType('all');
      setSendEmail(false);
      
      // Refresh announcements list
      fetchAnnouncements();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send announcement';
      
      // Show helpful error with suggestion
      if (errorMsg.includes('No') && errorMsg.includes('found')) {
        toast.error(errorMsg + '\n\nTip: Try selecting "All Users" to send to everyone.', {
          duration: 6000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedOption = recipientOptions.find(opt => opt.value === recipientType);
  const Icon = selectedOption?.icon || Users;

  const fetchAnnouncements = async () => {
    setFetchingAnnouncements(true);
    try {
      const response = await api.get('/admin/announcements');
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setFetchingAnnouncements(false);
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setEditTitle(announcement.title);
    setEditMessage(announcement.message);
    setEditModalOpen(true);
  };

  const handleUpdateAnnouncement = async () => {
    if (!editTitle.trim() || !editMessage.trim()) {
      toast.error('Title and message cannot be empty');
      return;
    }

    setUpdating(true);
    try {
      const response = await api.put(
        `/admin/announcements/${encodeURIComponent(editingAnnouncement.title)}`,
        {
          title: editTitle.trim(),
          message: editMessage.trim(),
          recipient_type: 'all', // Not used in update, but required by model
          send_email: false
        }
      );
      toast.success(`Updated ${response.data.updated_count} notification(s)`);
      setEditModalOpen(false);
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update announcement');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId, announcementTitle) => {
    if (!window.confirm(`Are you sure you want to delete the announcement "${announcementTitle}"? This will remove it from all users' notifications.`)) {
      return;
    }

    setDeletingId(announcementId);
    try {
      const response = await api.delete(`/admin/announcements/${announcementId}`);
      toast.success(`Deleted ${response.data.notifications_deleted} notification(s)`);
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete announcement');
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-emerald-500 to-blue-500 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-2">
            <Megaphone className="w-8 h-8" />
            <h2 className="text-2xl font-bold">Send Announcement</h2>
          </div>
          <p className="text-emerald-50">
            Broadcast updates and important messages to your users via in-app notifications and email
          </p>
        </CardContent>
      </Card>

      {/* Announcement Form */}
      <Card>
        <CardHeader>
          <CardTitle>Create Announcement</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Announcement Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Feature Released!"
              maxLength={100}
              className="text-base"
            />
            <p className="text-xs text-gray-500">{title.length}/100 characters</p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your announcement message here... Be clear and concise."
              rows={6}
              maxLength={1000}
              className="text-base resize-none"
            />
            <p className="text-xs text-gray-500">{message.length}/1000 characters</p>
          </div>

          {/* Recipient Type */}
          <div className="space-y-2">
            <Label>Who should receive this announcement? *</Label>
            <Select value={recipientType} onValueChange={setRecipientType}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {recipientOptions.map((option) => {
                  const OptionIcon = option.icon;
                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <OptionIcon className="w-4 h-4" />
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-500">{option.description}</div>
                        </div>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Selected recipient info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Icon className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <strong>{selectedOption?.label}:</strong> {selectedOption?.description}
              </div>
            </div>
            
            {/* Testing tip */}
            {(recipientType === 'trial' || recipientType === 'active' || recipientType === 'expired') && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-900">
                  <strong>Testing:</strong> If no users match this filter, try "All Users" to send to everyone.
                </div>
              </div>
            )}
          </div>

          {/* Send Email Option */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
              />
              <Label htmlFor="sendEmail" className="cursor-pointer font-normal">
                Also send via email
              </Label>
            </div>
            
            {sendEmail && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <Mail className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <strong>Email will be sent to all selected users.</strong> They will receive the announcement 
                  in their inbox in addition to the in-app notification.
                </div>
              </div>
            )}
            
            {!sendEmail && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2">
                <Bell className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-700">
                  <strong>In-app notification only.</strong> Users will see the announcement when they 
                  login to the app, but won't receive an email.
                </div>
              </div>
            )}
          </div>

          {/* Send Button */}
          <div className="pt-4 border-t">
            <Button
              onClick={handleSendAnnouncement}
              disabled={loading || !title.trim() || !message.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-6 text-lg"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending Announcement...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Send Announcement
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      {(title || message) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gradient-to-br from-blue-50 to-emerald-50 rounded-lg p-4 border-2 border-blue-200">
              <div className="flex items-start gap-3">
                <div className="bg-blue-500 rounded-full p-2">
                  <Bell className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {title || 'Announcement Title'}
                  </h3>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {message || 'Your announcement message will appear here...'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2">Just now</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sent Announcements */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Sent Announcements
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAnnouncements}
              disabled={fetchingAnnouncements}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${fetchingAnnouncements ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {fetchingAnnouncements ? (
            <div className="text-center py-8 text-gray-500">Loading announcements...</div>
          ) : announcements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No announcements sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {announcements.map((announcement, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Megaphone className="w-4 h-4 text-blue-600" />
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {announcement.title}
                        </h3>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 whitespace-pre-wrap">
                        {announcement.message}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>
                          Sent: {new Date(announcement.created_at).toLocaleDateString('en-PH', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {announcement.recipient_count} recipients
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditAnnouncement(announcement)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAnnouncement(announcement.id, announcement.title)}
                        disabled={deletingId === announcement.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deletingId === announcement.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="bg-emerald-50 border-emerald-200">
        <CardHeader>
          <CardTitle className="text-emerald-900 text-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Tips for Effective Announcements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-emerald-800">
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold">•</span>
              <span><strong>Keep it concise:</strong> Users are more likely to read short, clear messages</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold">•</span>
              <span><strong>Be specific:</strong> Include dates, version numbers, or clear action items</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold">•</span>
              <span><strong>Use email for important updates:</strong> Critical announcements should include email</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold">•</span>
              <span><strong>Target the right audience:</strong> Don't spam all users if update only affects trials</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-600 font-bold">•</span>
              <span><strong>Time it right:</strong> Send during business hours for better engagement</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Edit Announcement Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Announcement</DialogTitle>
            <DialogDescription>
              Update the announcement. Changes will be applied to all users' notifications.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Edit Title */}
            <div className="space-y-2">
              <Label htmlFor="edit-title">Announcement Title *</Label>
              <Input
                id="edit-title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="e.g., System Maintenance Notice"
                maxLength={100}
                className="text-base"
              />
              <p className="text-xs text-gray-500">{editTitle.length}/100 characters</p>
            </div>

            {/* Edit Message */}
            <div className="space-y-2">
              <Label htmlFor="edit-message">Message *</Label>
              <Textarea
                id="edit-message"
                value={editMessage}
                onChange={(e) => setEditMessage(e.target.value)}
                placeholder="Update your announcement message..."
                rows={6}
                maxLength={1000}
                className="text-base resize-none"
              />
              <p className="text-xs text-gray-500">{editMessage.length}/1000 characters</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <strong>ℹ️ Note:</strong> This will update the announcement for{' '}
                <strong>{editingAnnouncement?.recipient_count || 0} users</strong>. 
                All users will see the updated title and message in their notifications.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              disabled={updating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAnnouncement}
              disabled={updating || !editTitle.trim() || !editMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {updating ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Updating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Update Announcement
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
